import {
	AotCategory,
	AotCategoryType,
	logConstants,
	logConstantsType,
} from '../utils/SettingsLogger';
import { SettingsLogger } from '../utils/SettingsLogger';
import { SettingsNativeBridgeBase } from './SettingsNativeBridgeBase';
import {
	isPlatformEntrypointSupported,
	getSupportedEntrypoints,
} from '../config/SettingsPlatformConfig';
import {
	GetSettingParams,
	GetSettingsParams,
	SetSettingParams,
	SetSettingsParams,
	InitializeParams,
	GetSettingResponse,
	GetSettingsResponse,
	SetSettingResponse,
	SetSettingsResponse,
	InitializeResponse,
} from '../Contracts/index';
import type { WriteTraceParams } from '../utils/SettingsLogger';

// Extended window type declarations for native bridge integration
declare global {
	interface Window {
		webkit?: {
			messageHandlers?: {
				settingsNative?: {
					postMessage: (message: unknown) => void;
				};
			};
		};
		Android?: {
			settingsNative?: {
				postMessage: (message: string) => void;
			};
		};
	}
}

export class SettingsNativeBridgeWrapper extends SettingsNativeBridgeBase {
	private static _instance: SettingsNativeBridgeWrapper;

	private constructor() {
		super();
	}

	static get Instance(): SettingsNativeBridgeWrapper {
		return (
			SettingsNativeBridgeWrapper._instance ||
			(SettingsNativeBridgeWrapper._instance = new SettingsNativeBridgeWrapper())
		);
	}

	/**
	 * Handle a refresh request for a specific setting (Native → Web callback)
	 * Dispatches to all registered onRefreshSetting subscribers.
	 * @param settingKey - The key of the setting to refresh
	 */
	refreshSetting(settingKey: string): void {
		SettingsLogger.getInstance().write(
			logConstants.INFO,
			`[Wrapper] Received refreshSetting for key: ${settingKey}`,
			AotCategory.Info,
			`[Wrapper] Received refreshSetting for key: ${settingKey}`
		);
		this._onRefreshSettingCallbacks.forEach((callback) => callback(settingKey));
	}

	/**
	 * Initialize the settings bridge with initialize parameters
	 */
	async initialize(initializeParams: InitializeParams): Promise<InitializeResponse> {
		return new Promise<InitializeResponse>((resolve, reject) => {
			try {
				const { platform, settingsEntrypoint, systemInfo } = initializeParams;

				// Validate platform and entrypoint combination
				if (!isPlatformEntrypointSupported(platform, settingsEntrypoint)) {
					const supportedEntrypoints = getSupportedEntrypoints(platform);
					const error = `[Wrapper] Platform '${platform}' does not support entrypoint '${settingsEntrypoint}'. Supported entrypoints: ${supportedEntrypoints.join(
						', '
					)}`;
					SettingsLogger.getInstance().write(
						logConstants.ERROR,
						error,
						AotCategory.Error,
						error
					);
					reject(new Error(error));
					return;
				}

				this._platform = platform;
				this._entrypoint = settingsEntrypoint;
				this._systemInfo = systemInfo;
				this._isInitialized = true;
				SettingsLogger.getInstance().write(
					logConstants.INFO,
					`[Wrapper] Initialized successfully - Platform: ${platform}, Entry: ${settingsEntrypoint}, Version: ${systemInfo.version}`,
					AotCategory.Info,
					`[Wrapper] Initialized successfully - Platform: ${platform}, Entry: ${settingsEntrypoint}, Version: ${systemInfo.version}`
				);

				// Notify all registered callbacks
				this._onInitializedCallbacks.forEach((callback) => callback(initializeParams));

				resolve({
					status: 0,
					data: {
						platform,
						settingsEntrypoint,
						systemInfo,
					},
				});
			} catch (error) {
				SettingsLogger.getInstance().write(
					logConstants.ERROR,
					`[Wrapper] Initialization failed: ${
						error instanceof Error ? error.message : 'Unknown error'
					}`,
					AotCategory.Error,
					`[Wrapper] Initialization failed: ${
						error instanceof Error ? error.message : 'Unknown error'
					}`
				);
				reject(
					error instanceof Error ? error : new Error('Unknown initialization error')
				);
			}
		});
	}

	async getSetting(params: GetSettingParams): Promise<GetSettingResponse> {
		return new Promise<GetSettingResponse>((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`[Wrapper] Invoking getSetting with id: ${reqId} and params: ${JSON.stringify(
					params
				)}`,
				AotCategory.Info,
				`[Wrapper] Invoking getSetting with id: ${reqId} and params: ${JSON.stringify(
					params
				)}`
			);
			this.postMessageToNative('getSetting', reqId, JSON.stringify(params));
		});
	}

	async getSettings(params: GetSettingsParams): Promise<GetSettingsResponse> {
		return new Promise<GetSettingsResponse>((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`[Wrapper] Invoking getSettings with id: ${reqId} and params: ${JSON.stringify(
					params
				)}`,
				AotCategory.Info,
				`[Wrapper] Invoking getSettings with id: ${reqId} and params: ${JSON.stringify(
					params
				)}`
			);
			this.postMessageToNative('getSettings', reqId, JSON.stringify(params));
		});
	}

	async setSetting(params: SetSettingParams): Promise<SetSettingResponse> {
		return new Promise<SetSettingResponse>((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`[Wrapper] Invoking setSetting with id: ${reqId} and params: ${JSON.stringify(
					params
				)}`,
				AotCategory.Info,
				`[Wrapper] Invoking setSetting with id: ${reqId} and params: ${JSON.stringify(
					params
				)}`
			);
			this.postMessageToNative('setSetting', reqId, JSON.stringify(params));
		});
	}

	async setSettings(params: SetSettingsParams): Promise<SetSettingsResponse> {
		return new Promise<SetSettingsResponse>((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`[Wrapper] Invoking setSettings with id: ${reqId} and params: ${JSON.stringify(
					params
				)}`,
				AotCategory.Info,
				`[Wrapper] Invoking setSettings with id: ${reqId} and params: ${JSON.stringify(
					params
				)}`
			);
			this.postMessageToNative('setSettings', reqId, JSON.stringify(params));
		});
	}

	writeTrace(
		level: logConstantsType,
		message: string,
		aotCategory?: AotCategoryType,
		aoTrace?: string
	): void {
		const params: WriteTraceParams = { level, aotCategory, message, aotMessage: aoTrace };
		this.postMessageToNative('writeTrace', undefined, JSON.stringify(params));
	}

	settingsReady(): void {
		SettingsLogger.getInstance().write(
			logConstants.INFO,
			`[Wrapper] Invoking settingsReady`,
			AotCategory.Info,
			`[Wrapper] Invoking settingsReady`
		);
		this.postMessageToNative('settingsReady');
	}

	onInitializeComplete(id: string, response: InitializeResponse): void {
		const responseStr = JSON.stringify(response);
		SettingsLogger.getInstance().write(
			logConstants.INFO,
			`[Wrapper] Invoking onInitializeComplete with id: ${id} and response: ${responseStr}`,
			AotCategory.Info,
			`[Wrapper] Invoking onInitializeComplete with id: ${id} and response: ${responseStr}`
		);
		this.postMessageToNative('onInitializeComplete', id, responseStr);
	}

	onGetSettingComplete(id: string, response: GetSettingResponse): void {
		const responseStr = JSON.stringify(response);
		SettingsLogger.getInstance().write(
			logConstants.INFO,
			`[Wrapper] Invoking onGetSettingComplete with id: ${id} and response: ${responseStr}`,
			AotCategory.Info,
			`[Wrapper] Invoking onGetSettingComplete with id: ${id} and response: ${responseStr}`
		);
		this.postMessageToNative('onGetSettingComplete', id, responseStr);
	}

	onGetSettingsComplete(id: string, response: GetSettingsResponse): void {
		const responseStr = JSON.stringify(response);
		SettingsLogger.getInstance().write(
			logConstants.INFO,
			`[Wrapper] Invoking onGetSettingsComplete with id: ${id} and response: ${responseStr}`,
			AotCategory.Info,
			`[Wrapper] Invoking onGetSettingsComplete with id: ${id} and response: ${responseStr}`
		);
		this.postMessageToNative('onGetSettingsComplete', id, responseStr);
	}

	onSetSettingComplete(id: string, response: SetSettingResponse): void {
		const responseStr = JSON.stringify(response);
		SettingsLogger.getInstance().write(
			logConstants.INFO,
			`[Wrapper] Invoking onSetSettingComplete with id: ${id} and response: ${responseStr}`,
			AotCategory.Info,
			`[Wrapper] Invoking onSetSettingComplete with id: ${id} and response: ${responseStr}`
		);
		this.postMessageToNative('onSetSettingComplete', id, responseStr);
	}

	onSetSettingsComplete(id: string, response: SetSettingsResponse): void {
		const responseStr = JSON.stringify(response);
		SettingsLogger.getInstance().write(
			logConstants.INFO,
			`[Wrapper] Invoking onSetSettingsComplete with id: ${id} and response: ${responseStr}`,
			AotCategory.Info,
			`[Wrapper] Invoking onSetSettingsComplete with id: ${id} and response: ${responseStr}`
		);
		this.postMessageToNative('onSetSettingsComplete', id, responseStr);
	}

	private postMessageToNative(method: string, id?: string, data?: string): void {
		const message = {
			method,
			id,
			data,
			timestamp: Date.now(),
		};

		if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
			// For iframe scenarios
			window.parent.postMessage(JSON.stringify(message), '*');
		} else if (
			typeof window.webkit !== 'undefined' &&
			window.webkit.messageHandlers &&
			window.webkit.messageHandlers.settingsNative
		) {
			// For iOS WKWebView
			window.webkit.messageHandlers.settingsNative.postMessage(message);
		} else if (typeof window.Android !== 'undefined' && window.Android.settingsNative) {
			// For Android WebView
			window.Android.settingsNative.postMessage(JSON.stringify(message));
		} else {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`[Wrapper] No native bridge available for method: ${method}`,
				AotCategory.Error,
				`[Wrapper] No native bridge available for method: ${method}`
			);
		}
	}
}
